import {
    Table,
    TableHeader,
    TableRow,
    TableSelectionCell,
    TableHeaderCell,
    TableBody,
    TableCell,
    TableCellLayout,
    useTableFeatures,
    useTableColumnSizing_unstable,
    TableColumnSizingOptions,
    useTableSelection,
    useTableSort,
    TableColumnDefinition,
    createTableColumn,
    TableColumnId,
} from "@fluentui/react-components";
import { FC, useCallback, useMemo, useState, KeyboardEvent, useEffect } from "react";
import "./ExplorerTable.scss"
import { formatAipSize } from "components/aip/format";
import { AipLevelType } from "elza-api";
import { useNodeName } from "../levels";
import { ExplorerMode, useExplorerContext } from "../ExplorerContext";
import { useThunkDispatch } from "utils/hooks";
import { setSelectedAipDaos } from "actions/aip/aip";
import { FormattedMessage, useIntl } from "react-intl";
import { explorerMessages } from "../../messages";

type Item = {
    filename?: string;
    label?: string;
    levelType?: AipLevelType;
    size?: number;
    mimeType?: string;
}

/**
 * Sloupce se skládají až v komponentě - název virtuální úrovně se překládá, takže ho i řazení
 * musí brát z překladu, ne z popisku ze serveru.
 */
const buildColumns = (nodeName: (item: Item) => string): TableColumnDefinition<Item>[] => [
    createTableColumn<Item>({
      columnId: "name",
      renderHeaderCell: () => <FormattedMessage {...explorerMessages.colName} />,
      renderCell: (item) => <>{nodeName(item) || "-"}</>,
      compare: (a, b) => nodeName(a).localeCompare(nodeName(b)),
    }),
    createTableColumn<Item>({
      columnId: "size",
      renderHeaderCell: () => <FormattedMessage {...explorerMessages.colSize} />,
      renderCell: (item) => <>{item.size ? formatAipSize(item.size) : "-"}</>,
      compare: (a, b) => b.size - a.size
    }),
    createTableColumn<Item>({
      columnId: "format",
      renderHeaderCell: () => <FormattedMessage {...explorerMessages.colFormat} />,
      renderCell: (item) => <>{item.mimeType || "-"}</>,
      compare: (a, b) => a.mimeType?.localeCompare(b.mimeType)
    }),
];

const columnSizes = {
    name: {idealWidth: 300, minWidth: 50},
    size: {idealWidth: 200, minWidth: 50},
    format: {idealWidth: 300, minWidth: 50}
}

const ExplorerTable: FC = () => {
    const intl = useIntl();
    const {selectedItem, setSelectedItem, mode} = useExplorerContext();
    const [items, setItems] = useState([]);
    const [columnSizingOptions] = useState<TableColumnSizingOptions>(columnSizes);
    const dispatch = useThunkDispatch();
    const nodeName = useNodeName();
    const columns = useMemo(() => buildColumns(nodeName), [nodeName]);

    useEffect(() => {
        let newItems = [];

        if(selectedItem?.filename) {
            newItems = [...selectedItem?.parent?.childFolders || [], ...selectedItem?.parent?.childFiles || []]
        } else {
            newItems = [...selectedItem?.childFolders  || [], ...selectedItem?.childFiles  || []];
        }

        setItems(newItems);
    }, [selectedItem]);

    const {
        getRows,
        columnSizing_unstable,
        tableRef,
        sort: { getSortDirection, toggleColumnSort, sort },
        selection: {
            allRowsSelected,
            someRowsSelected,
            toggleAllRows,
            toggleRow,
            isRowSelected,
          },
    } = useTableFeatures(
        { columns, items },
        [
            useTableColumnSizing_unstable({ columnSizingOptions }),
            useTableSort({defaultSortState: { sortColumn: "id", sortDirection: "ascending"}}),
            useTableSelection({
                selectionMode: "multiselect",
                onSelectionChange: (e, data) => {
                    const selectedRows = rows
                        .filter(row => data.selectedItems.has(row.rowId))
                        .map(row => row.item.daoId);
                        dispatch(setSelectedAipDaos(selectedRows))
                }
            })
        ]
      );

    const rows = sort(getRows((row) => {
        const selected = isRowSelected(row.rowId);
        return {
            ...row,
            onClick: (e: MouseEvent) => {
                 //@ts-ignore
                toggleRow(e, row.rowId);
            },
            onKeyDown: (e: KeyboardEvent) => {
                if (e.key === " ") {
                    e.preventDefault();
                    toggleRow(e, row.rowId);
                }
            },
            selected,
            appearance: selected ? ("brand" as const) : ("none" as const),
        };
    }));

    const headerSortProps = (columnId: TableColumnId) => ({
        onClick: (e: MouseEvent) => {
             //@ts-ignore
            toggleColumnSort(e, columnId);
        },
        sortDirection: getSortDirection(columnId),
    });

    const toggleAllKeydown = useCallback(
        (e: KeyboardEvent<HTMLDivElement>) => {
            if (e.key === " ") {
                toggleAllRows(e);
                e.preventDefault();
            }
        },
        [toggleAllRows]
    );

    const handleSelect = (item: Item) => {
        setSelectedItem(item)
    }

    return (
        <Table
            ref={tableRef}
            as="table"
            sortable
            {...columnSizing_unstable.getTableProps()}
            className="explorer-table"
        >
            <TableHeader>
                <TableRow>
                    {mode == ExplorerMode.SELECT && <TableSelectionCell
                        checked={allRowsSelected ? true : someRowsSelected ? "mixed" : false}
                        onClick={toggleAllRows}
                        onKeyDown={toggleAllKeydown}
                        checkboxIndicator={{"aria-label": intl.formatMessage(explorerMessages.selectAll)}}
                        className="header"

                    />}
                    {columns.map((column) => (
                            //@ts-ignore
                        <TableHeaderCell
                            key={column.columnId}
                            {...columnSizing_unstable.getTableHeaderCellProps(column.columnId)}
                            {...headerSortProps(column.columnId)}
                            className="header"
                        >
                            {column.renderHeaderCell()}
                        </TableHeaderCell>
                    ))}
                </TableRow>
            </TableHeader>
            <TableBody>
                {rows.map(({ item, selected, onClick }, index) => (
                    <TableRow
                        key={`${item.uuid}`}
                        className="table-row"
                        style={{backgroundColor: item.uuid == selectedItem.uuid ? "#ddd": undefined}}
                    >
                        {mode == ExplorerMode.SELECT && <TableSelectionCell
                            checked={selected}
                            checkboxIndicator={{ "aria-label": "Vybrat" }}
                                //@ts-ignore
                            onClick={onClick}
                        />}

                        {columns.map(col => (
                            <TableCell
                                key={`item.${col.columnId}`}
                                {...columnSizing_unstable.getTableCellProps(col.columnId)}
                                onClick={() => handleSelect(item)}
                            >
                                <TableCellLayout truncate>
                                    {col.renderCell(item)}
                                </TableCellLayout>
                        </TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

export default ExplorerTable;
